"use strict";
/**
 * Tally and Timeline View State Manager
 *
 * Pure functions for managing tally data (day/week) and timeline/details display state.
 * These functions are stateless, side-effect-free, and easily testable.
 *
 * Design principles:
 * - All functions are pure (no side effects)
 * - State is passed in and modified in place (for performance)
 * - No dependencies on external state or services
 * - Fully unit tested (see tallyStateManager.test.ts)
 *
 * Manages:
 * - Tally data state (day/week summaries, details, timeline)
 * - Display mode state (timeline/details visibility toggles)
 * - Data availability checks
 *
 * Usage:
 * ```typescript
 * import { clearTallyData, resetDisplayState, setTimelineOnlyMode } from './services/tallyStateManager';
 *
 * // When switching days, clear old data and reset UI state
 * clearTallyData(tallyState);
 * resetDisplayState(viewMode);
 *
 * // Toggle between timeline/details views
 * setTimelineOnlyMode(viewMode);
 * ```
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.toggleDetails = exports.toggleTimeline = exports.isDetailsTableVisible = exports.isTimelineVisible = exports.getCurrentMode = exports.setNoneMode = exports.setBothMode = exports.setDetailsOnlyMode = exports.setTimelineOnlyMode = exports.getTimelineButtonLabel = exports.getDetailsButtonLabel = exports.hasTimelineAvailable = exports.hasDetailsAvailable = exports.updateDisplayStateFromData = exports.shouldShowTimeline = exports.shouldShowDetails = exports.clearTallyData = exports.resetDisplayState = void 0;
/**
 * Reset display state to initial values (nothing shown)
 */
function resetDisplayState(viewMode) {
    viewMode.displayMode = 'none';
    viewMode.showDetails = false;
    viewMode.showTimeline = false;
}
exports.resetDisplayState = resetDisplayState;
/**
 * Clear tally data objects
 */
function clearTallyData(dailyTally) {
    dailyTally.details = null;
    dailyTally.timeline = null;
}
exports.clearTallyData = clearTallyData;
/**
 * Determine if details should be shown based on data availability and view mode
 */
function shouldShowDetails(dailyTally, viewMode) {
    var hasDetails = !!(dailyTally.details && dailyTally.details.length > 0);
    var displayModeWantsDetails = viewMode.displayMode === 'details' || viewMode.displayMode === 'both';
    return hasDetails && displayModeWantsDetails;
}
exports.shouldShowDetails = shouldShowDetails;
/**
 * Determine if timeline should be shown based on data availability and view mode
 */
function shouldShowTimeline(dailyTally, viewMode) {
    var hasTimeline = dailyTally.timeline != null;
    var displayModeWantsTimeline = viewMode.displayMode === 'timeline' || viewMode.displayMode === 'both';
    return hasTimeline && displayModeWantsTimeline;
}
exports.shouldShowTimeline = shouldShowTimeline;
/**
 * Update display state based on loaded data availability
 * Resets display flags if data is not available
 */
function updateDisplayStateFromData(dailyTally, viewMode) {
    var hasDetails = dailyTally.details && dailyTally.details.length > 0;
    var hasTimeline = dailyTally.timeline != null;
    // Reset showDetails if no data
    if (!hasDetails) {
        viewMode.showDetails = false;
    }
    // Reset showTimeline if no data
    if (!hasTimeline) {
        viewMode.showTimeline = false;
    }
    // Reset displayMode if it references missing data
    if (viewMode.displayMode === 'details' && !hasDetails) {
        viewMode.displayMode = undefined;
    }
    else if (viewMode.displayMode === 'timeline' && !hasTimeline) {
        viewMode.displayMode = undefined;
    }
    else if (viewMode.displayMode === 'both' && !hasDetails && !hasTimeline) {
        viewMode.displayMode = undefined;
    }
}
exports.updateDisplayStateFromData = updateDisplayStateFromData;
/**
 * Check if details are available for display
 */
function hasDetailsAvailable(dailyTally) {
    return !!(dailyTally.details && dailyTally.details.length > 0);
}
exports.hasDetailsAvailable = hasDetailsAvailable;
/**
 * Check if timeline is available for display
 */
function hasTimelineAvailable(dailyTally) {
    return dailyTally.timeline != null;
}
exports.hasTimelineAvailable = hasTimelineAvailable;
/**
 * Get button label based on current state
 */
function getDetailsButtonLabel(dailyTally, viewMode) {
    return shouldShowDetails(dailyTally, viewMode) ? 'Hide details' : 'Show details';
}
exports.getDetailsButtonLabel = getDetailsButtonLabel;
/**
 * Get timeline button label based on current state
 */
function getTimelineButtonLabel(dailyTally, viewMode) {
    return shouldShowTimeline(dailyTally, viewMode) ? 'Hide timeline' : 'Show timeline';
}
exports.getTimelineButtonLabel = getTimelineButtonLabel;
// ============================================================================
// Display Mode Management
// ============================================================================
/**
 * Set timeline-only mode
 */
function setTimelineOnlyMode(viewMode) {
    viewMode.displayMode = 'timeline';
    viewMode.showTimeline = true;
    viewMode.showDetails = true; // Keep container visible
}
exports.setTimelineOnlyMode = setTimelineOnlyMode;
/**
 * Set details-only mode
 */
function setDetailsOnlyMode(viewMode) {
    viewMode.displayMode = 'details';
    viewMode.showTimeline = false;
    viewMode.showDetails = true;
}
exports.setDetailsOnlyMode = setDetailsOnlyMode;
/**
 * Set both timeline and details visible
 */
function setBothMode(viewMode) {
    viewMode.displayMode = 'both';
    viewMode.showTimeline = true;
    viewMode.showDetails = true;
}
exports.setBothMode = setBothMode;
/**
 * Hide both timeline and details
 */
function setNoneMode(viewMode) {
    viewMode.displayMode = 'none';
    viewMode.showTimeline = false;
    viewMode.showDetails = false;
}
exports.setNoneMode = setNoneMode;
/**
 * Get current display mode
 */
function getCurrentMode(viewMode) {
    return viewMode.displayMode || 'none';
}
exports.getCurrentMode = getCurrentMode;
/**
 * Check if timeline is currently visible
 */
function isTimelineVisible(viewMode) {
    var mode = getCurrentMode(viewMode);
    return mode === 'timeline' || mode === 'both';
}
exports.isTimelineVisible = isTimelineVisible;
/**
 * Check if details table is currently visible
 */
function isDetailsTableVisible(viewMode) {
    var mode = getCurrentMode(viewMode);
    return mode === 'details' || mode === 'both';
}
exports.isDetailsTableVisible = isDetailsTableVisible;
/**
 * Toggle timeline visibility
 */
function toggleTimeline(viewMode) {
    if (isTimelineVisible(viewMode)) {
        if (getCurrentMode(viewMode) === 'timeline') {
            setNoneMode(viewMode);
        }
        else if (getCurrentMode(viewMode) === 'both') {
            setDetailsOnlyMode(viewMode);
        }
    }
    else {
        if (getCurrentMode(viewMode) === 'details') {
            setBothMode(viewMode);
        }
        else {
            setTimelineOnlyMode(viewMode);
        }
    }
}
exports.toggleTimeline = toggleTimeline;
/**
 * Toggle details table visibility
 */
function toggleDetails(viewMode) {
    if (isDetailsTableVisible(viewMode)) {
        if (getCurrentMode(viewMode) === 'details') {
            setNoneMode(viewMode);
        }
        else if (getCurrentMode(viewMode) === 'both') {
            setTimelineOnlyMode(viewMode);
        }
    }
    else {
        if (getCurrentMode(viewMode) === 'timeline') {
            setBothMode(viewMode);
        }
        else {
            setDetailsOnlyMode(viewMode);
        }
    }
}
exports.toggleDetails = toggleDetails;
